export type Locale = "en" | "he" | "ru";
export type LocaleSetting = Locale | "auto";

export interface Translations {
  // Common
  save: string;
  cancel: string;
  delete: string;
  loading: string;
  back: string;
  search: string;
  close: string;
  confirm: string;
  edit: string;
  create: string;
  copy: string;
  copied: string;
  download: string;
  upload: string;
  settings: string;
  profile: string;
  logout: string;
  login: string;
  signup: string;

  // Navigation
  nav_home: string;
  nav_explore: string;
  nav_community: string;
  nav_messages: string;
  nav_settings: string;
  nav_profile: string;

  // Settings - Appearance
  settings_appearance: string;
  settings_appearance_desc: string;
  settings_theme: string;
  settings_theme_desc: string;
  settings_theme_light: string;
  settings_theme_light_desc: string;
  settings_theme_dark: string;
  settings_theme_dark_desc: string;
  settings_theme_system: string;
  settings_theme_system_desc: string;
  settings_language: string;
  settings_language_desc: string;
  locale_auto_detect: string;
  settings_login_bg: string;
  settings_login_bg_desc: string;
  settings_login_bg_url_placeholder: string;
  settings_login_bg_hint: string;

  // Settings - AI
  settings_ai: string;
  settings_ai_desc: string;
  settings_provider: string;
  settings_provider_desc: string;
  settings_use_own_key: string;
  settings_using_own_key: string;
  settings_using_lovable: string;
  settings_lovable_ready: string;
  settings_test_connection: string;
  settings_test_key: string;
  settings_test_lovable: string;
  settings_testing: string;

  // Settings - Voice
  settings_voice: string;
  settings_voice_desc: string;
  settings_tts_provider: string;
  settings_tts_provider_desc: string;
  settings_default_voice: string;
  settings_available_voices: string;

  // Settings - Credits
  settings_credits: string;
  settings_credits_desc: string;

  // Settings - Analytics
  settings_analytics: string;
  settings_analytics_desc: string;

  // Settings - Tools
  settings_tools: string;
  settings_tools_desc: string;

  // Settings - tabs
  tab_appearance: string;
  tab_ai_provider: string;
  tab_credits: string;
  tab_voice: string;
  tab_music_library: string;
  tab_analytics: string;
  tab_error_logs: string;
  tab_tools: string;

  // Profile
  profile_title: string;
  profile_display_name: string;
  profile_email: string;
  profile_bio: string;
  profile_signature: string;
  profile_public: string;
  profile_avatar: string;
  profile_change_password: string;
  profile_current_password: string;
  profile_new_password: string;
  profile_confirm_password: string;
  profile_update: string;
  profile_friends: string;
  profile_delete_account: string;
  profile_delete_confirm: string;

  // Dashboard
  dashboard_worlds: string;
  dashboard_adventures: string;
  dashboard_create_world: string;
  dashboard_no_worlds: string;
  dashboard_no_adventures: string;

  // Play
  play_your_turn: string;
  play_send: string;
  play_type_action: string;

  // World Builder
  world_builder: string;
  world_entities: string;
  world_plots: string;
  world_mechanics: string;
  world_scenarios: string;

  // Misc
  no_results: string;
  error_occurred: string;
  success: string;

  // ── Auth ──
  auth_sign_in: string;
  auth_sign_up: string;
  auth_signing_in: string;
  auth_signing_up: string;
  auth_sign_in_subtitle: string;
  auth_sign_up_subtitle: string;
  auth_reset_password: string;
  auth_reset_subtitle: string;
  auth_forgot_password: string;
  auth_send_reset_link: string;
  auth_sending: string;
  auth_no_account: string;
  auth_has_account: string;
  auth_back_to_sign_in: string;
  auth_email: string;
  auth_password: string;
  auth_display_name: string;
  auth_email_placeholder: string;
  auth_password_placeholder: string;
  auth_password_signup_placeholder: string;
  auth_password_min: string;
  auth_name_placeholder: string;
  auth_referral_bonus: string;
  auth_daily_bonus: string;
  auth_welcome_back: string;
  auth_check_email: string;
  auth_reset_sent: string;
  auth_enter_email: string;

  // ── AppHeader / Nav ──
  nav_worlds: string;
  nav_about: string;
  nav_roadmap: string;
  nav_pricing: string;
  nav_contact: string;
  nav_forums: string;
  nav_users: string;
  nav_sign_in_up: string;
  nav_guest_browsing: string;
  nav_set_display_name: string;
  nav_user_management: string;
  nav_sign_out: string;
  nav_active_tasks: string;
  nav_clear: string;
  gh_open_menu: string;
  gh_menu: string;
  gh_account: string;
  gh_credits: string;
  nav_your_credits: string;
  nav_no_credits: string;
  nav_low_credits: string;
  nav_transfers_disabled: string;

  // Task phases
  task_searching: string;
  task_writing_brief: string;
  task_generating: string;
  task_complete: string;
  task_failed: string;

  // ── Index / Dashboard (extended) ──
  idx_tagline: string;
  idx_my_worlds: string;
  idx_my_adventures: string;
  idx_public_worlds: string;
  idx_trash: string;
  idx_new_world: string;
  idx_create_new_world: string;
  idx_manual: string;
  idx_manual_desc: string;
  idx_ai_prompt: string;
  idx_ai_prompt_desc: string;
  idx_world_name_placeholder: string;
  idx_world_desc_placeholder: string;
  idx_world_prompt_placeholder: string;
  idx_reference_files: string;
  idx_terms_title: string;
  idx_terms_content_1: string;
  idx_terms_content_2: string;
  idx_create_with_ai: string;
  idx_create_and_build: string;
  idx_no_worlds_hint: string;
  idx_your_worlds: string;
  idx_shared_with_you: string;
  idx_new_game: string;
  idx_join_game: string;
  idx_join_game_title: string;
  idx_join_game_desc: string;
  idx_invite_code_placeholder: string;
  idx_join: string;
  idx_joining_adventure: string;
  idx_select_world: string;
  idx_choose_world: string;
  idx_no_worlds_available: string;
  idx_select_or_new_adventure: string;
  idx_or: string;
  idx_your_turn: string;
  idx_adventures: string;
  idx_no_adventures_hint: string;
  idx_trashed_worlds: string;
  idx_trash_desc: string;
  idx_trash_empty: string;
  idx_restore: string;
  idx_delete_forever: string;
  idx_delete_forever_title: string;
  idx_delete_forever_desc: string;
  idx_no_public_worlds: string;
  idx_solo: string;
  idx_joined: string;
  idx_yours: string;
  idx_updated: string;
  idx_deleted: string;
  idx_turn: string;
  idx_drop_files: string;
  idx_start_new_game: string;

  // ── Profile (extended) ──
  profile_account_settings: string;
  profile_account_settings_desc: string;
  profile_info: string;
  profile_info_desc: string;
  profile_achievements: string;
  profile_avatar_hint: string;
  profile_name_placeholder: string;
  profile_name_hint: string;
  profile_email_readonly: string;
  profile_bio_placeholder: string;
  profile_avatar_url_placeholder: string;
  profile_signature_placeholder: string;
  profile_signature_hint: string;
  profile_public_hint: string;
  profile_save_changes: string;
  profile_updating: string;
  profile_password_desc: string;
  profile_password_changing: string;
  profile_account_actions: string;
  profile_account_actions_desc: string;
  profile_logout_confirm: string;
  profile_logout_confirm_desc: string;

  // ── Play ──
  play_what_do_you_do: string;
  play_waiting_turn: string;
  play_debug_log: string;
  play_adventure_state: string;

  // ── World Builder (extended) ──
  wb_title: string;
  wb_empty_desc: string;
  wb_describe_placeholder: string;
  wb_describe_placeholder_full: string;
  wb_shuffle: string;
  wb_show_older: string;
  wb_queue: string;
  wb_queue_paused: string;
  wb_send_queue: string;
  wb_resume: string;
  wb_pause: string;
  wb_unpause: string;
  wb_clear: string;
  wb_stop: string;
  wb_retry: string;
  wb_unsaved: string;
  wb_unsaved_title: string;
  wb_unsaved_desc: string;
  wb_leave_without_saving: string;
  wb_save_and_leave: string;
  wb_read_only: string;
  wb_update: string;
  wb_full_access: string;
  wb_debug_dump: string;
  wb_panel_title: string;

  // ── World Builder Panel (CreationPanel) ──
  wb_entities: string;
  wb_save: string;
  wb_saved: string;
  wb_play: string;
  wb_play_adventure: string;
  wb_ui_version_label: string;
  wb_ui_version_desc: string;
  wb_ui_version_v1: string;
  wb_ui_version_v2: string;
  flow_version_label: string;
  flow_version_desc: string;
  flow_version_v1: string;
  flow_version_v2: string;
  wb_v2_canvas_hint: string;
  wb_v2_empty: string;
  wb_v2_actions: string;
  wb_v2_action_add: string;
  wb_v2_action_generate: string;
  wb_v2_action_layout: string;
  wb_v2_action_settings: string;
  wb_v2_coming_soon: string;
  wb_loading_adventures: string;
  wb_continue_playing: string;
  wb_or_start_new: string;
  wb_adventure_name: string;
  wb_multiplayer: string;
  wb_multiplayer_desc: string;
  wb_turn_mode: string;
  wb_free_form: string;
  wb_round_robin: string;
  wb_create_mp: string;
  wb_begin_new: string;
  wb_share: string;
  wb_world_banner: string;
  wb_trash: string;
  wb_section_build: string;
  wb_section_statistics: string;
  wb_section_feedback: string;
  wb_section_publishing: string;
  wb_group_general: string;
  wb_group_data: string;
  wb_group_media: string;
  wb_group_engine: string;
  wb_group_agents: string;
  wb_tab_world_gen: string;
  wb_tab_checklist: string;
  wb_tab_locations: string;
  wb_tab_map: string;
  wb_tab_entities: string;
  wb_tab_plot: string;
  wb_tab_context: string;
  wb_tab_media: string;
  wb_tab_voice: string;
  wb_tab_music: string;
  wb_tab_mechanics: string;
  wb_tab_scenarios: string;
  wb_tab_players: string;
  wb_tab_agents: string;
  wb_tab_chats: string;
  wb_tab_about: string;
  wb_tab_privacy: string;
  wb_tab_actions: string;
  wb_tab_design_issues: string;
  wb_quick_create: string;
  wb_describe_create: string;
  wb_send: string;
  wb_tab_promotion: string;
  wb_section_instances: string;
  wb_group_general_desc: string;
  wb_group_data_desc: string;
  wb_group_media_desc: string;
  wb_group_engine_desc: string;
  wb_group_agents_desc: string;
  wb_quick_new_entity: string;
  wb_quick_new_plot: string;
  wb_quick_new_agent: string;
  wb_quick_new_location: string;

  // ── World AI Provider ──
  ai_title: string;
  ai_desc: string;
  ai_membership_required: string;
  ai_membership_desc: string;
  ai_use_custom: string;
  ai_use_custom_desc: string;
  ai_no_accounts: string;
  ai_no_accounts_desc: string;
  ai_account_label: string;
  ai_select_account: string;
  ai_default_account: string;
  ai_account_hint: string;
  ai_save_config: string;
  ai_config_saved: string;
  ai_save_failed: string;
  ai_verified: string;
  ai_default_badge: string;

  // ── Plot Panel ──
  plot_cat_main_quest: string;
  plot_cat_side_quest: string;
  plot_cat_plot_hook: string;
  plot_cat_conflict: string;
  plot_cat_key_moment: string;
  plot_cat_event: string;
  plot_cat_development: string;
  plot_cat_lore: string;
  plot_cat_completion: string;
  plot_cat_note: string;
  plot_tab_quests: string;
  plot_tab_quests_desc: string;
  plot_tab_story: string;
  plot_tab_story_desc: string;
  plot_tab_conditions: string;
  plot_tab_conditions_desc: string;
  plot_relevance_critical: string;
  plot_relevance_high: string;
  plot_relevance_medium: string;
  plot_relevance_low: string;
  plot_relevance_background: string;
  plot_objectives: string;
  plot_add_objective: string;
  plot_entry_title: string;
  plot_add_entry: string;
  plot_no_quests: string;
  plot_no_story: string;
  plot_no_conditions: string;
  plot_add_first: string;
  plot_title_placeholder: string;
  plot_describe_placeholder: string;
  plot_category: string;
  plot_relevance: string;
  plot_linked_scenario: string;
  plot_none: string;
  plot_no_description: string;
  plot_all_scenarios: string;
  plot_aria_filter_scenario: string;
  plot_aria_new_entry_category: string;
  plot_aria_edit_category: string;
  plot_aria_linked_scenario: string;
  plot_merge: string;
  plot_selected: string;
  plot_select_min_2: string;
  plot_merge_failed: string;
  plot_deleted: string;
  plot_delete_failed: string;
  plot_save_failed: string;
  plot_add_failed: string;

  // ── Design Issues Panel ──
  di_loading: string;
  di_no_issues: string;
  di_no_issues_desc: string;
  di_title: string;
  di_open: string;
  di_desc: string;
  di_cat_entity: string;
  di_cat_mechanic: string;
  di_cat_prompt: string;
  di_cat_tool: string;
  di_cat_content: string;
  di_dismiss: string;
  di_occurrences: string;
  di_first_seen_turn: string;
  di_source_ai: string;
  di_source_rule: string;

  // ── Privacy Section (extras) ──
  priv_confidential: string;
  priv_confidential_desc: string;
  priv_confidential_active: string;
  priv_confidential_active_desc: string;
  priv_locked_by_confidential: string;
  priv_share_link: string;
  priv_share_link_desc: string;
  priv_share_copied: string;

  // ── Generation Section (extras) ──
  gen_conservative: string;
  gen_balanced: string;
  gen_aggressive: string;
  gen_conservative_desc: string;
  gen_balanced_desc: string;
  gen_aggressive_desc: string;
  gen_conservative_detail: string;
  gen_balanced_detail: string;
  gen_aggressive_detail: string;

  // ── Context Panel ──
  ctx_description: string;
  ctx_click_to_edit: string;
  ctx_click_to_add: string;
  ctx_media_generation: string;
  ctx_allow_ingame: string;
  ctx_allow_ingame_desc: string;
  ctx_media_enabled: string;
  ctx_media_disabled: string;
  ctx_update_failed: string;

  // ── Scenarios Panel ──
  scn_desc: string;
  scn_new: string;
  scn_no_scenarios: string;
  scn_no_scenarios_desc: string;
  scn_create_first: string;
  scn_needs_title: string;
  scn_updated: string;
  scn_created: string;
  scn_default_set: string;
  scn_deleted: string;
  scn_delete_failed: string;
  scn_difficulty_easy: string;
  scn_difficulty_normal: string;
  scn_difficulty_hard: string;
  scn_difficulty_brutal: string;
  scn_difficulty_adaptive: string;
  scn_img_saved: string;
  scn_img_save_failed: string;
  scn_img_removed: string;
  scn_img_remove_failed: string;

  // ── About Section (editor) ──
  about_preview_page: string;
  about_click_change_icon: string;
  about_click_set: string;
  about_world_name_hint: string;
  about_world_id_copied: string;
  about_anonymous: string;

  // ── Actions Section (editor) ──
  act_world_deleted: string;
  act_delete_failed: string;
  act_clone_placeholder: string;
  act_clone_failed: string;
  act_cloned: string;

  // ── Gameplay panels ──
  play_auto: string;
  play_manual: string;
  play_no_response: string;
  play_send_again: string;
  play_may_not_received: string;
  play_you: string;
  play_turn: string;
  play_active: string;
  play_switch: string;
  play_no_pov: string;
  play_no_party: string;
  play_party: string;
  play_waiting_char: string;
  play_inner_voice: string;
  play_thoughts_log: string;
  play_observing: string;
  play_awaiting_thoughts: string;

  // ── Decision Journal ──
  play_decision_journal: string;
  play_pending_consequences: string;
  play_choices_pending: string;
  play_immediate: string;
  play_ripple_effects: string;

  // ── Encounter / Events ──
  play_no_events: string;
  play_no_events_desc: string;
  play_events_world: string;
  play_active_events: string;
  play_pending_ripples: string;

  // ── Lore Codex ──
  play_codex_characters: string;
  play_codex_locations: string;
  play_codex_quests: string;
  play_codex_factions: string;
  play_codex_items: string;
  play_codex_lore: string;

  // ── Research Panel ──
  play_research_tree: string;
  play_research_in_progress: string;
  play_research_queued: string;
  play_research_completed: string;
  play_research_none: string;
  play_research_ask: string;

  // ── Operations Panel ──
  play_ops_overview: string;
  play_ops_territory: string;
  play_ops_workforce: string;
  play_ops_resources: string;
  play_ops_unnamed_org: string;

  // ── Org tiers/types/styles ──
  play_tier_street: string;
  play_tier_established: string;
  play_tier_major: string;
  play_tier_dominant: string;
  play_tier_undisputed: string;
  play_org_criminal: string;
  play_org_intelligence: string;
  play_org_corporate: string;
  play_org_military: string;
  play_org_custom: string;
  play_style_hierarchical: string;
  play_style_cell: string;
  play_style_feudal: string;

  // ── Item rarity ──
  play_rarity_common: string;
  play_rarity_uncommon: string;
  play_rarity_rare: string;
  play_rarity_epic: string;
  play_rarity_legendary: string;

  // ── Dice Tray ──
  play_quick_rolls: string;
  play_dice_hint: string;

  // ── Location detail ──
  play_known_details: string;
  play_travel_to: string;

  // ── Codex extra ──
  play_codex_empty: string;
  play_codex_empty_desc: string;
  play_codex_title: string;
  play_codex_search: string;
  play_codex_other: string;

  // ── Encounter extra ──
  play_enc_all: string;
  play_enc_encounters: string;
  play_enc_news: string;
  play_enc_ripples: string;
  play_enc_no_encounters: string;
  play_enc_no_news: string;
  play_enc_since_event: string;
  play_enc_since_news: string;

  // ── Research extra ──
  play_research_tier: string;
  play_research_turn_left: string;

  // ── Org / Operations shared ──
  play_org_unnamed: string;
  play_org_treasury: string;
  play_org_income_turn: string;
  play_org_tier: string;
  play_org_morale: string;
  play_org_heat: string;
  play_org_territories: string;
  play_org_personnel: string;
  play_org_resources: string;
  play_org_no_territory: string;
  play_org_no_personnel: string;
  play_org_no_resources: string;
  play_org_controlled: string;
  play_org_contested: string;
  play_org_rival_factions: string;
  play_org_stockpile: string;
  play_org_production: string;
  play_org_upkeep: string;
  play_org_controlled_loc: string;
  play_org_no_territory_ctrl: string;

  // ── Item card ──
  play_item_equipped: string;
  play_item_modifiers: string;
  play_item_when_equipped: string;
  play_item_effects: string;
  play_item_durability: string;
  play_item_charges: string;
  play_item_weight: string;
  play_item_cursed: string;
  play_item_quest: string;

  // ── Recent thoughts ──
  play_recent_thoughts: string;

  // ── PlayChatInput extra ──
  play_autoscroll_on: string;
  play_autoscroll_off: string;
  play_debug_downloads: string;
  play_conversation_log: string;
  play_game_snapshot: string;

  // ── Locations Panel (editor) ──
  loc_open_map_editor: string;
  loc_maps: string;
  loc_no_maps: string;
  loc_new_map: string;
  loc_map_editor: string;

  // ── Play widgets ──
  widget_rest_title: string;
  widget_rest_hit_dice: string;
  widget_rest_last_long: string;
  widget_rest_today: string;
  widget_rest_days_ago: string;
  widget_rest_short: string;
  widget_rest_long: string;
  widget_combat_death_saves: string;
  widget_combat_attack: string;
  widget_combat_defend: string;
  widget_combat_flee: string;
  widget_combat_special: string;
  widget_combat_attack_desc: string;
  widget_combat_defend_desc: string;
  widget_combat_flee_desc: string;
  widget_combat_special_desc: string;
  widget_conditions_title: string;
  widget_conditions_none: string;
  widget_conditions_exhaustion: string;
  widget_conditions_concentration: string;
  widget_conditions_turns_left: string;

  // ── About page ──
  about_hero_title_1: string;
  about_hero_title_2: string;
  about_hero_desc: string;
  about_hero_desc_em: string;
  about_hero_cta_dashboard: string;
  about_hero_cta_start: string;
  about_hero_cta_browse: string;
  about_bottom_title: string;
  about_bottom_desc: string;
  about_badge_worldbuilding: string;
  about_badge_gamemaster: string;
  about_badge_survival: string;
  about_badge_npcs: string;
  about_badge_timetravel: string;
  about_badge_multiplayer: string;
  about_sub_worldbuilding: string;
  about_sub_gamemaster: string;
  about_sub_survival: string;
  about_sub_npcs: string;
  about_sub_timetravel: string;
  about_sub_multiplayer: string;
  about_title_worldbuilding: string;
  about_title_gamemaster: string;
  about_title_survival: string;
  about_title_npcs: string;
  about_title_timetravel: string;
  about_title_multiplayer: string;
  about_desc_worldbuilding_1: string;
  about_desc_worldbuilding_2: string;
  about_desc_worldbuilding_3: string;
  about_desc_gamemaster_1: string;
  about_desc_gamemaster_2: string;
  about_desc_gamemaster_3: string;
  about_desc_survival_1: string;
  about_desc_survival_2: string;
  about_desc_survival_3: string;
  about_desc_npcs_1: string;
  about_desc_npcs_2: string;
  about_desc_npcs_3: string;
  about_desc_timetravel_1: string;
  about_desc_timetravel_2: string;
  about_desc_timetravel_3: string;
  about_desc_multiplayer_1: string;
  about_desc_multiplayer_2: string;
  about_desc_multiplayer_3: string;

  // ── BYOK section keys ──
  about_badge_byok: string;
  about_sub_byok: string;
  about_title_byok: string;
  about_desc_byok_1: string;
  about_desc_byok_2: string;
  about_desc_byok_3: string;

  // ── About FAQ section ──
  about_faq_title: string;
  about_faq_q1: string;
  about_faq_a1: string;
  about_faq_q2: string;
  about_faq_a2: string;
  about_faq_q3: string;
  about_faq_a3: string;
  about_faq_q4: string;
  about_faq_a4: string;
  about_faq_q5: string;
  about_faq_a5: string;
  about_faq_q6: string;
  about_faq_a6: string;

  explore_byok_title: string;
  explore_byok_desc: string;
  explore_byok_cta: string;
  explore_byok_feature_1: string;
  explore_byok_feature_2: string;
  explore_byok_feature_3: string;
  explore_byok_feature_4: string;
  pricing_byok_title: string;
  pricing_byok_desc: string;
  pricing_byok_price: string;
  pricing_byok_per_month: string;
  pricing_byok_feature_1: string;
  pricing_byok_feature_2: string;
  pricing_byok_feature_3: string;
  pricing_byok_feature_4: string;
  pricing_byok_feature_5: string;
  pricing_byok_note: string;
  pricing_byok_cta: string;

  // ── Explore page ──
  explore_hero_title_1: string;
  explore_hero_title_2: string;
  explore_hero_desc: string;
  explore_cta_start: string;
  explore_cta_dashboard: string;
  explore_cta_browse: string;
  explore_how_compare: string;
  explore_how_compare_desc: string;
  explore_feature: string;
  explore_show_more: string;
  explore_show_less: string;
  explore_comparison_note: string;
  explore_what_create: string;
  explore_what_create_desc: string;
  explore_mechanics_title: string;
  explore_mechanics_desc: string;
  explore_alpha_title: string;
  explore_alpha_desc: string;
  explore_ready_title: string;
  explore_ready_desc: string;
  explore_get_started: string;

  // ── Roadmap page ──
  roadmap_badge: string;
  roadmap_title: string;
  roadmap_desc: string;
  roadmap_features: string;
  roadmap_cta_title: string;
  roadmap_cta_desc: string;
  roadmap_cta_forums: string;
  roadmap_cta_explore: string;
  roadmap_status_live: string;
  roadmap_status_beta: string;
  roadmap_status_alpha: string;
  roadmap_status_planned: string;
  roadmap_status_exploring: string;
  roadmap_item_world_builder: string;
  roadmap_item_world_builder_desc: string;
  roadmap_item_mechanics: string;
  roadmap_item_mechanics_desc: string;
  roadmap_item_narrator: string;
  roadmap_item_narrator_desc: string;
  roadmap_item_image_gen: string;
  roadmap_item_image_gen_desc: string;
  roadmap_item_tts: string;
  roadmap_item_tts_desc: string;
  roadmap_item_document: string;
  roadmap_item_document_desc: string;
  roadmap_item_journal: string;
  roadmap_item_journal_desc: string;
  roadmap_item_npc_agents: string;
  roadmap_item_npc_agents_desc: string;
  roadmap_item_quest_journal: string;
  roadmap_item_quest_journal_desc: string;
  roadmap_item_fan_fiction: string;
  roadmap_item_fan_fiction_desc: string;
  roadmap_item_custom_rules: string;
  roadmap_item_custom_rules_desc: string;
  roadmap_item_forums: string;
  roadmap_item_forums_desc: string;
  roadmap_item_research: string;
  roadmap_item_research_desc: string;
  roadmap_item_multiplayer: string;
  roadmap_item_multiplayer_desc: string;
  roadmap_item_world_map: string;
  roadmap_item_world_map_desc: string;
  roadmap_item_map_library: string;
  roadmap_item_map_library_desc: string;
  roadmap_item_byoai: string;
  roadmap_item_byoai_desc: string;
  roadmap_item_music: string;
  roadmap_item_music_desc: string;
  roadmap_item_mobile: string;
  roadmap_item_mobile_desc: string;
  roadmap_item_marketplace: string;
  roadmap_item_marketplace_desc: string;
  roadmap_item_voice_chat: string;
  roadmap_item_voice_chat_desc: string;
  roadmap_item_campaign: string;
  roadmap_item_campaign_desc: string;
  roadmap_item_pdf_export: string;
  roadmap_item_pdf_export_desc: string;
  roadmap_item_streaming: string;
  roadmap_item_streaming_desc: string;
  roadmap_item_video: string;
  roadmap_item_video_desc: string;
  roadmap_item_vr: string;
  roadmap_item_vr_desc: string;
  roadmap_item_procgen: string;
  roadmap_item_procgen_desc: string;

  // ── Support Us / Pricing page ──
  support_badge: string;
  support_title: string;
  support_desc: string;
  support_best_value: string;
  support_get_pack: string;
  support_what_buy: string;
  support_ai_narration: string;
  support_ai_narration_desc: string;
  support_world_building: string;
  support_world_building_desc: string;
  support_image_voice: string;
  support_image_voice_desc: string;
  support_xp_title: string;
  support_xp_desc: string;
  support_title_label: string;
  support_lifetime_badge: string;
  support_free: string;
  support_pack_trial: string;
  support_pack_trial_desc: string;
  support_pack_starter: string;
  support_pack_starter_desc: string;
  support_pack_adventurer: string;
  support_pack_adventurer_desc: string;
  support_pack_hero: string;
  support_pack_hero_desc: string;
  support_pack_legend: string;
  support_pack_legend_desc: string;

  // ── Contact page ──
  contact_badge: string;
  contact_title: string;
  contact_desc: string;
  contact_send_message: string;
  contact_respond_time: string;
  contact_name: string;
  contact_name_placeholder: string;
  contact_email: string;
  contact_email_placeholder: string;
  contact_subject: string;
  contact_subject_placeholder: string;
  contact_message: string;
  contact_message_placeholder: string;
  contact_send: string;
  contact_github: string;
  contact_github_desc: string;
  contact_community: string;
  contact_community_desc: string;
  contact_support_cta: string;
  contact_support_cta_desc: string;
  contact_fill_required: string;
  contact_sent_success: string;
  contact_sent_error: string;

  // ── Community page ──
  community_forums: string;
  community_loading: string;
  forum_title: string;
  forum_col_forum: string;
  forum_col_topics: string;
  forum_col_posts: string;
  forum_col_last_post: string;
  forum_no_categories: string;
  forum_no_posts_yet: string;
  forum_stats_title: string;
  forum_total_topics: string;
  forum_total_posts: string;
  forum_total_members: string;
  forum_newest_member: string;
  forum_by: string;
  forum_start_new_topic: string;
  forum_create_topic_title: string;
  forum_topic_title_placeholder: string;
  forum_first_post_placeholder: string;
  forum_post_anonymously: string;
  forum_create_topic: string;
  forum_no_topics: string;
  forum_no_topics_desc: string;
  forum_col_topic: string;
  forum_col_replies: string;
  forum_col_views: string;
  forum_pinned_topics: string;
  forum_topics: string;
  forum_posts_count: string;
  forum_views_count: string;
  forum_locked: string;
  forum_topic_not_found: string;
  forum_edited: string;
  forum_unpin: string;
  forum_pin: string;
  forum_unlock: string;
  forum_lock: string;
  forum_save: string;
  forum_cancel: string;
  forum_quote: string;
  forum_reply_placeholder: string;
  forum_post_reply: string;
  forum_topic_locked: string;
  forum_joined: string;
  forum_member_veteran: string;
  forum_member_regular: string;
  forum_member_active: string;
  forum_member_member: string;
  forum_member_newcomer: string;
  forum_time_just_now: string;
  forum_time_minutes_ago: string;
  forum_time_hours_ago: string;
  forum_time_days_ago: string;
  forum_post_updated: string;
  forum_update_failed: string;
  forum_action_failed: string;
  forum_react_failed: string;

  // ── Not Found page ──
  notfound_title: string;
  notfound_desc: string;
  notfound_back: string;

  // ── Gameplay UI ──
  play_anonymous: string;
  play_you_suffix: string;
  play_turn_label: string;
  play_invite_copied: string;
  play_present: string;
  play_scene_tags: string;
  play_day: string;
  play_time: string;
  play_mode: string;
  play_combat: string;
  play_phase: string;
  play_initiative_order: string;
  play_player_character: string;
  play_hp: string;
  play_level: string;
  play_gold: string;
  play_no_character: string;
  play_character: string;
  play_world: string;
  play_journal: string;
  play_director: string;
  play_party_chat: string;
  play_no_messages: string;
  play_message_party: string;
  play_send_failed: string;
  play_portrait: string;
  play_conditions: string;
  play_inventory_empty: string;
  play_weight: string;
  play_encumbered: string;
  play_heavy_load: string;
  play_overburdened: string;
  play_base: string;
  play_active_bonuses: string;
  play_equipped: string;
  play_available: string;
  play_complete: string;
  play_failed: string;
  play_create_quest: string;
  play_quest_goal: string;
  play_go: string;
  play_no_quests: string;
  play_accept: string;
  play_rewards: string;
  play_personal: string;
  play_diary: string;
  play_story: string;
  play_no_adventure: string;
  play_no_entries: string;
  play_write_hint: string;
  play_story_caught_up: string;
  play_story_style_placeholder: string;
  play_action_details: string;
  play_critical: string;
  play_fumble: string;
  play_lore_codex: string;
  play_search_lore: string;
  play_edit_world: string;
  play_edit_template: string;
  play_edit_game: string;
  play_editing_game_banner: string;
  play_editing_game_subtitle: string;
  play_mode_editor: string;
  play_mode_play: string;
  play_mode_switch_aria: string;
  play_multiplayer: string;
  play_your_turn_badge: string;
  play_other_turn: string;
  play_waiting_other: string;
  play_hidden_messages: string;
  play_reclassify_fail: string;
  play_reclassify_success: string;
  play_continue_story: string;
  play_rewrite_all: string;
  play_chapter: string;
  play_entry: string;
  play_new_story: string;
  play_journal_entry: string;
  play_read_full: string;
  play_no_data: string;

  // ── Multiplayer Communication ──
  mp_tab_chat: string;
  mp_tab_board: string;
  mp_tab_interactions: string;
  mp_board_title: string;
  mp_no_threads: string;
  mp_new_thread: string;
  mp_thread_title: string;
  mp_write_post: string;
  mp_create_thread: string;
  mp_reply_placeholder: string;
  mp_interactions_title: string;
  mp_no_interactions: string;
  mp_new_interaction: string;
  mp_trade: string;
  mp_gift: string;
  mp_challenge: string;
  mp_alliance: string;
  mp_select_player: string;
  mp_offered_items: string;
  mp_requested_items: string;
  mp_offered_gold: string;
  mp_requested_gold: string;
  mp_interaction_message: string;
  mp_offering: string;
  mp_requesting: string;
  mp_decline: string;
  mp_not_enough_gold: string;
  mp_leave_adventure: string;
  mp_leave_failed: string;
  mp_left_adventure: string;
  mp_kick_failed: string;
  mp_player_kicked: string;
  mp_kick_confirm: string;
  mp_kick: string;
  mp_spectator: string;
  mp_you_badge: string;
  mp_host_badge: string;
  mp_anonymous_player: string;
  mp_your_char_name: string;
  mp_waiting_for_host: string;
  mp_invite_code_copied: string;
  mp_invite_link_copied: string;
  mp_share_invite_code: string;
  mp_share_invite_link: string;
  mp_turn_free_form: string;
  mp_turn_concurrent: string;
  mp_turn_initiative: string;
  mp_turn_round_robin: string;

  // ── Builder: General Panel (About) ──
  wb_gen_about: string;
  wb_gen_about_desc: string;
  wb_gen_featured_image: string;
  wb_gen_change_banner: string;
  wb_gen_generate_banner: string;
  wb_gen_icon: string;
  wb_gen_world_name: string;
  wb_gen_world_name_placeholder: string;
  wb_gen_description: string;
  wb_gen_description_placeholder: string;
  wb_gen_promo_title: string;
  wb_gen_promo_desc: string;
  wb_gen_promo_generate: string;
  wb_gen_promo_regenerate: string;
  wb_gen_promo_seed: string;
  wb_gen_promo_seed_placeholder: string;
  wb_gen_promo_style: string;
  wb_gen_promo_length: string;
  wb_gen_promo_style_professional: string;
  wb_gen_promo_style_epic: string;
  wb_gen_promo_style_mysterious: string;
  wb_gen_promo_style_casual: string;
  wb_gen_promo_length_short: string;
  wb_gen_promo_length_medium: string;
  wb_gen_promo_length_long: string;
  wb_gen_promo_analyzing: string;
  wb_gen_promo_copy: string;
  wb_gen_promo_use_desc: string;
  wb_gen_genre_themes: string;
  wb_gen_auto_detect: string;
  wb_gen_genre: string;
  wb_gen_genre_placeholder: string;
  wb_gen_themes: string;
  wb_gen_themes_placeholder: string;
  wb_gen_content_language: string;
  wb_gen_content_language_desc: string;
  wb_gen_tags: string;
  wb_gen_add_tag: string;
  wb_gen_add: string;
  wb_gen_translations: string;
  wb_gen_translate_now: string;
  wb_gen_auto_translate: string;
  wb_gen_auto_translate_desc: string;
  wb_gen_primary_language: string;
  wb_gen_info: string;
  wb_gen_world_id: string;
  wb_gen_created_by: string;
  wb_gen_created: string;
  wb_gen_saving: string;
  wb_gen_save_changes: string;
  wb_gen_saved: string;
  wb_gen_name_label: string;

  // ── Builder: General Panel (Generation) ──
  wb_gen_world_generation: string;
  wb_gen_world_generation_desc: string;
  wb_gen_level_conservative: string;
  wb_gen_level_balanced: string;
  wb_gen_level_aggressive: string;
  wb_gen_level_active: string;
  wb_gen_auditor: string;
  wb_gen_auditor_desc: string;
  wb_gen_enable_auditor: string;
  wb_gen_auditor_runs: string;
  wb_gen_audit_interval: string;
  wb_gen_audit_frequent: string;
  wb_gen_audit_rare: string;

  // ── Builder: General Panel (Privacy) ──
  wb_gen_privacy: string;
  wb_gen_privacy_desc: string;
  wb_gen_under_dev: string;
  wb_gen_under_dev_desc: string;
  wb_gen_public_world: string;
  wb_gen_public_world_desc: string;
  wb_gen_invite_only: string;
  wb_gen_invite_only_desc: string;
  wb_gen_allow_cloning: string;
  wb_gen_allow_cloning_desc: string;
  wb_gen_pricing: string;
  wb_gen_pricing_desc: string;
  wb_gen_ticket_cost: string;
  wb_gen_ticket_cost_desc: string;
  wb_gen_clone_price: string;
  wb_gen_clone_price_desc: string;
  wb_gen_min_xp: string;
  wb_gen_min_xp_desc: string;
  wb_gen_free_for_players: string;
  wb_gen_free_for_players_desc: string;

  // ── Builder: General Panel (Actions) ──
  wb_gen_actions: string;
  wb_gen_actions_desc: string;
  wb_gen_clone_world: string;
  wb_gen_delete_world: string;
  wb_gen_clone_dialog_title: string;
  wb_gen_clone_dialog_desc: string;
  wb_gen_new_world_name: string;
  wb_gen_clone_btn: string;
  wb_gen_delete_dialog_title: string;
  wb_gen_delete_dialog_desc: string;
  wb_gen_delete_cannot_undo: string;
  wb_gen_delete_permanently: string;

  // ── Builder: Entities Panel ──
  wb_ent_no_entities: string;
  wb_ent_no_entities_desc: string;
  wb_ent_search: string;
  wb_ent_no_match: string;

  // ── Builder: Plot Panel ──
  wb_plot_quests: string;
  wb_plot_story: string;
  wb_plot_conditions: string;
  wb_plot_quests_desc: string;
  wb_plot_story_desc: string;
  wb_plot_conditions_desc: string;
  wb_plot_objectives: string;
  wb_plot_add_objective: string;
  wb_plot_relevance_critical: string;
  wb_plot_relevance_high: string;
  wb_plot_relevance_medium: string;
  wb_plot_relevance_low: string;
  wb_plot_relevance_background: string;
  wb_plot_new_entry: string;
  wb_plot_no_entries: string;

  // ── Builder: Scenarios Panel ──
  wb_sc_desc: string;
  wb_sc_new: string;
  wb_sc_no_scenarios: string;
  wb_sc_no_scenarios_desc: string;
  wb_sc_create_first: string;
  wb_sc_default: string;
  wb_sc_set_default: string;
  wb_sc_title: string;
  wb_sc_description: string;
  wb_sc_opening: string;
  wb_sc_starting_location: string;
  wb_sc_difficulty: string;
  wb_sc_allow_custom: string;
  wb_sc_guidelines: string;
  wb_sc_progressions: string;
  wb_sc_add_progression: string;
  wb_sc_characters: string;
  wb_sc_tags: string;
  wb_sc_needs_title: string;

  // ── Builder: Players Panel ──
  wb_pc_title: string;
  wb_pc_desc: string;
  wb_pc_new: string;
  wb_pc_no_characters: string;
  wb_pc_no_characters_desc: string;
  wb_pc_create_first: string;
  wb_pc_identity: string;
  wb_pc_backstory: string;
  wb_pc_personality: string;
  wb_pc_stats: string;
  wb_pc_inventory: string;

  // ── Builder: Mechanics Panel ──
  wb_mech_search: string;
  wb_mech_allow_dynamic: string;
  wb_mech_allow_dynamic_desc: string;
  wb_mech_active: string;
  wb_mech_guidance_placeholder: string;

  // ── Builder: Agent Chat Dialog ──
  wb_agent_select_npc: string;
  wb_agent_describe_self: string;
  wb_agent_select_location: string;
  wb_agent_location_example: string;
  wb_agent_time_example: string;
  wb_agent_context_example: string;

  // ── Builder: Agent Editor ──
  wb_agent_no_linked_entity: string;
  wb_agent_traits_placeholder: string;
  wb_agent_values_placeholder: string;
  wb_agent_fears_placeholder: string;
  wb_agent_goals_placeholder: string;
  wb_agent_speech_patterns_placeholder: string;
  wb_agent_verbal_tics_placeholder: string;
  wb_agent_use_world_default: string;
  wb_agent_use_default_model: string;

  // ── Builder: Checklist Panel ──
  wb_checklist_task_name: string;
  wb_checklist_task_desc: string;
  wb_checklist_task_prompt: string;

  // ── Builder: Context Panel ──
  wb_context_desc_placeholder: string;

  // ── Builder: Custom Rules Editor ──
  wb_rule_title_placeholder: string;
  wb_rule_text_placeholder: string;

  // ── Builder: Entities Panel ──
  wb_ent_search_placeholder: string;

  // ── Builder: Entity Detail Dialog ──
  wb_ent_add_tag: string;
  wb_ent_json_data: string;
  wb_ent_refine_instructions: string;
  wb_ent_name_placeholder: string;
  wb_ent_tag_input: string;
  wb_ent_desc_detail: string;

  // ── Builder: Entity Property Helpers ──
  wb_ent_custom_field: string;

  // ── Builder: General Panel ──
  wb_gen_promo_section_placeholder: string;

  // ── Builder: Group Chat Dialog ──
  wb_group_location_placeholder: string;
  wb_group_time_placeholder: string;

  // ── Builder: Initial Threads Editor ──
  wb_thread_desc_placeholder: string;
  wb_thread_hook_placeholder: string;

  // ── Builder: Player Characters Panel ──
  wb_pc_backstory_placeholder: string;
  wb_pc_name_placeholder: string;
  wb_pc_class_placeholder: string;
  wb_pc_desc_placeholder: string;
  wb_pc_playstyle_placeholder: string;

  // ── Builder: Relationship Editor ──
  wb_rel_search_entities: string;
  wb_rel_custom_name: string;
  wb_rel_type_search: string;
  wb_rel_custom_type: string;
  wb_rel_desc: string;
  wb_rel_notes: string;

  // ── Builder: Scenarios Panel ──
  wb_sc_title_placeholder: string;
  wb_sc_desc_placeholder: string;
  wb_sc_opening_placeholder: string;
  wb_sc_location_placeholder: string;
  wb_sc_guidelines_placeholder: string;
  wb_sc_path_name: string;
  wb_sc_path_desc: string;
  wb_sc_item_name: string;
  wb_sc_item_qty: string;
  wb_sc_item_category: string;
  wb_sc_item_rarity: string;
  wb_sc_tags_placeholder: string;

  // ── Builder: Share World Dialog ──
  wb_share_email_placeholder: string;

  // ── Builder: Source References Panel ──
  wb_src_title_placeholder: string;
  wb_src_url_placeholder: string;
  wb_src_desc_placeholder: string;

  // ── Builder: Style References Panel ──
  wb_style_label_placeholder: string;

  // ── Builder: Template Selector ──
  wb_template_search: string;

  // ── Builder: Voice Controls Panel ──
  wb_voice_select_placeholder: string;
  wb_voice_id_placeholder_venice: string;
  wb_voice_id_placeholder_elevenlabs: string;
  wb_voice_sample_placeholder: string;
  wb_voice_describe_placeholder: string;

  // ── Builder: Art Direction Tab ──
  wb_art_direction_placeholder: string;
  wb_art_brief_manual_placeholder: string;

  // ── Index / World Stats ──
  idx_characters: string;
  idx_scenarios: string;
  idx_entities: string;
  idx_all_worlds: string;
  idx_all_worlds_admin: string;

  // ── Header Credits ──
  hdr_credits: string;
  hdr_credit_wallet: string;
  hdr_usage_dashboard: string;
  hdr_pricing_rates: string;
  hdr_earn_credits: string;
  hdr_ai_disabled: string;
  hdr_building_disabled: string;

  // ── Reset Password page ──
  reset_title_done: string;
  reset_title_new: string;
  reset_desc_done: string;
  reset_desc_new: string;
  reset_redirecting: string;
  reset_link_expired: string;
  reset_back_sign_in: string;
  reset_new_password: string;
  reset_confirm_password: string;
  reset_placeholder: string;
  reset_re_enter: string;
  reset_updating: string;
  reset_update_button: string;
  reset_password_min: string;
  reset_passwords_mismatch: string;
  reset_success: string;

  // ── Auth extras ──
  auth_remember_me: string;
  auth_err_invalid_creds: string;
  auth_err_email_not_confirmed: string;
  auth_err_user_exists: string;
  auth_err_password_too_short: string;
  auth_err_rate_limit: string;
  auth_err_signup_disabled: string;
  auth_err_generic: string;

  // ── Common actions ──
  common_generating: string;
  common_processing: string;
  common_resume: string;
  common_pause: string;
  common_generate: string;
  common_saving: string;

  // ── World Builder: various panels ──
  wb_voice_save: string;
  wb_agent_auto_generate: string;
  wb_doc_generate_codex: string;
  wb_doc_status_vector: string;
  wb_doc_status_text: string;
  wb_doc_status_error: string;
  wb_doc_indexed: string;
  wb_doc_timeout: string;
  wb_doc_still_processing: string;
  wb_doc_error_processing: string;
  wb_map_rename: string;
  wb_map_upload_image: string;
  wb_map_upload_overlay: string;
  wb_map_generate_ai: string;

  // ── Play: Adventure setup ──
  play_setup_char_name: string;
  play_setup_char_desc: string;
  play_setup_adventure_name: string;
  gm_ask_placeholder: string;
  notes_title_placeholder: string;
  notes_content_placeholder: string;
  notes_untitled: string;
  notes_untitled_note: string;

  // ── Social: Friends panel ──
  social_search_placeholder: string;
  social_no_users: string;
  social_search_failed: string;
  social_find_players: string;
  social_friend_requests: string;
  social_friends: string;
  social_no_friends: string;
  social_sent_requests: string;

  // ── Profile extras ──
  profile_tab_byok: string;
  profile_secret_mode: string;
  profile_secret_mode_desc: string;

  // ── Play: TTS ──
  play_tts_disabled: string;

  // ── World Builder: rename ──
  wb_rename_failed: string;
  wb_rename_tooltip: string;

  // ── Messages / Direct Chat ──
  msg_page_title: string;
  msg_unknown_contact: string;
  chat_conversation_reset: string;
  chat_send_failed: string;
  chat_no_messages: string;
  chat_type_message: string;

  // ── Accessible button labels (aria-label) ──
  aria_send_message: string;
  aria_message_options: string;
  aria_edit_message: string;
  aria_delete_message: string;
  aria_roll_dice: string;
  aria_notifications: string;
  notif_mark_all_read: string;
  notif_empty: string;
  aria_pause_narration: string;
  aria_resume_narration: string;
  aria_stop_narration: string;
  aria_replay_narration: string;
  aria_next_track: string;

  // ── useMultiplayerAdventure ──
  adv_load_failed: string;
  adv_your_turn: string;
  adv_your_turn_desc: string;
  adv_create_failed: string;
  adv_invalid_invite: string;
  adv_joined: string;
  adv_join_failed: string;
  adv_no_retry_msg: string;
  adv_orphaned_removed: string;
  adv_msg_delete_failed: string;
  adv_msg_deleted: string;
  adv_msg_edit_failed: string;
  adv_msg_updated: string;

  // ── useFriends ──
  friends_request_exists: string;
  friends_request_failed: string;
  friends_request_sent: string;
  friends_accept_failed: string;
  friends_request_accepted: string;
  friends_decline_failed: string;
  friends_request_declined: string;
  friends_remove_failed: string;
  friends_removed: string;

  // ── useIndexData (Index page actions) ──
  idx_load_failed: string;
  idx_file_too_large: string;
  idx_file_read_failed: string;
  idx_need_credits: string;
  idx_create_world_failed: string;
  idx_world_trashed: string;
  idx_world_restored: string;
  idx_world_perm_deleted: string;
  idx_world_published: string;
  idx_world_unpublished: string;
  idx_world_cloned: string;
  idx_clone_failed: string;
  idx_adventure_deleted: string;
  idx_adventure_left: string;
  idx_adventure_joined: string;
  idx_invalid_invite: string;
  idx_invite_copied: string;
  idx_delete_failed: string;
  idx_restore_failed: string;
  idx_update_failed: string;
  idx_leave_failed: string;
  idx_join_failed: string;
  idx_start_game_failed: string;

  // ── DocumentUpload ──
  doc_file_too_large: string;
  doc_file_already_exists: string;
  doc_file_empty: string;
  doc_file_duplicate_content: string;
  doc_upload_success: string;
  doc_upload_failed: string;
  doc_no_supported_files: string;
  doc_deleted: string;
  doc_delete_failed: string;
  doc_removed_stuck: string;
  doc_reindexed: string;
  doc_reindex_failed: string;
  doc_codex_regenerated: string;
  doc_codex_failed: string;
  doc_reanalyzed: string;
  doc_reanalyze_failed: string;

  // ── ShareWorldDialog ──
  share_link_copied: string;
  share_copy_failed: string;
  share_load_collab_failed: string;
  share_user_not_found: string;
  share_cannot_add_self: string;
  share_already_collab: string;
  share_collab_added: string;
  share_add_collab_failed: string;
  share_permission_updated: string;
  share_update_failed: string;
  share_collab_removed: string;
  share_remove_failed: string;

  // ── useCreditBalance ──
  cred_img_disabled: string;
  cred_img_disabled_desc: string;
  cred_running_low: string;
  cred_running_low_desc: string;
  cred_voice_disabled: string;
  cred_voice_disabled_desc: string;
  cred_transfer_disabled: string;
  cred_transfer_disabled_desc: string;
  cred_build_disabled: string;
  cred_build_disabled_desc: string;
  cred_all_disabled: string;
  cred_all_disabled_desc: string;

  // ── GeneralPanel ──
  gen_world_saved: string;
  gen_save_failed: string;
  gen_promo_generated: string;
  gen_copied_clipboard: string;
  gen_intro_applied: string;
  gen_all_langs_translated: string;
  gen_translations_generated: string;
  gen_generation_failed: string;
  gen_translation_failed: string;

  // ── WorldMapEditor ──
  map_layer_placed: string;
  map_polygon_created: string;
  map_connection_added: string;
  map_placed_locations: string;
  map_failed_suffix: string;
  map_geocode_failed: string;
  map_all_placed: string;
  map_cannot_resolve: string;
  map_moved_via: string;
  map_fix_failed: string;
  map_variant_created: string;
  map_variant_duplicated: string;
  map_connection_removed: string;
  map_added_layer: string;
  map_add_layer_failed: string;
  map_drawing_saved: string;
  map_save_drawing_failed: string;
  map_ai_layer_generated: string;
  map_ai_no_image: string;
  map_ai_generation_failed: string;
  map_switched_to: string;
  map_linked_to: string;
  map_unlinked: string;
  map_placed_coords: string;
  map_geocode_not_found: string;
  map_geocode_error: string;
  map_placed_via: string;
  map_default_view_saved: string;

  // ── import-utils ──
  imp_loading_world: string;
  imp_invalid_world_file: string;
  imp_sign_in_required: string;
  imp_world_success: string;
  imp_failed: string;
  imp_loading_game: string;
  imp_invalid_game_file: string;
  imp_game_success: string;
  imp_resetting: string;
  imp_reset_success: string;
  imp_reset_failed: string;

  // ── export-utils ──
  exp_loading_world: string;
  exp_world_success: string;
  exp_failed: string;
  exp_loading_player: string;
  exp_player_success: string;
  exp_loading_game: string;
  exp_game_success: string;

  // ── ImageGenerationDialog ──
  img_gen_disabled: string;
  img_only_images: string;
  img_ref_uploaded: string;
  img_upload_failed: string;
  img_draft_loaded: string;
  img_draft_deleted: string;
  img_drawing_saved: string;
  img_drawing_save_failed: string;
  img_generated: string;
  // ── useWorldBuilderAgent ──
  wb_load_failed: string;
  wb_create_failed: string;
  wb_world_saved: string;
  wb_save_failed: string;
  wb_credits_required: string;
  wb_agent_error: string;
  // ── World ready celebration ──
  wb_world_ready_title: string;
  wb_world_ready_desc: string;
  // ── useTTS ──
  tts_no_voice: string;
  tts_no_voice_short: string;
  tts_downloaded: string;
  tts_download_failed: string;
  // ── useFeatureFlags ──
  ff_locked: string;
  ff_update_failed: string;
  ff_lock_failed: string;
  ff_update_flags_failed: string;
  // ── StyleReferencesPanel ──
  sref_added: string;
  sref_upload_failed: string;
  sref_delete_failed: string;
  sref_removed: string;
  // ── TrashManagerDialog ──
  trash_load_failed: string;
  trash_restored: string;
  trash_restore_failed: string;
  trash_deleted: string;
  trash_delete_failed: string;
  trash_emptied: string;
  trash_empty_failed: string;
  // ── ChecklistPanel ──
  check_title_required: string;
  check_updated: string;
  check_added: string;
  check_reset: string;
  check_all_done: string;
  check_queued: string;
  // ── ReportContentButton ──
  rep_sign_in_required: string;
  rep_already_reported: string;
  rep_submitted: string;
  rep_submit_failed: string;
  rep_try_later: string;
  // ── useMapVariants ──
  var_create_failed: string;
  var_update_failed: string;
  var_delete_last: string;
  var_delete_failed: string;
  var_active_failed: string;
  // ── useMapUploads ──
  upl_map_uploaded: string;
  upl_upload_failed: string;
  upl_layer_added: string;
  upl_layer_failed: string;
  upl_base_assigned: string;
  upl_map_applied: string;
  upl_svg_applied: string;
  // ── Map editor / canvas placeholders ──
  map_node_label_placeholder: string;
  map_lat_placeholder: string;
  map_lng_placeholder: string;
  map_address_placeholder: string;
  map_search_nodes_placeholder: string;
  map_connection_desc_placeholder: string;
  map_search_nodes_places_placeholder: string;
  map_location_name_placeholder: string;
  map_voice_default_placeholder: string;
  map_search_locations_placeholder: string;
  map_node_desc_placeholder: string;
  map_name_placeholder: string;
  map_all_locations_filter: string;
  map_node_image_url: string;
  map_node_tags_placeholder: string;
  map_style_json_url: string;
  // ── useMapNodes ──
  node_create_failed: string;
  node_delete_failed: string;
  node_save_partial: string;
  node_saved: string;
  node_save_failed: string;
  // ── MediaGallery ──
  media_img_generated: string;
  media_generation_failed: string;
  media_delete_failed: string;
  media_update_failed: string;
  media_set_main: string;
  media_disconnect_failed: string;
  media_archived: string;
  // ── EntityDetailDialog ──
  ent_updated: string;
  ent_save_failed: string;
  ent_trashed: string;
  ent_delete_failed: string;
  ent_id_copied: string;
  ent_deactivated: string;
  ent_activated: string;
  ent_dormant: string;
  ent_active: string;
  // ── CreditWalletPanel ──
  cw_invalid_amount: string;
  cw_balance_low: string;
  cw_min_transfer: string;
  cw_insufficient: string;
  cw_sent: string;
  // ── AgentEditor / AgentChatDialog / GroupChatDialog ──
  agent_needs_name: string;
  agent_saved: string;
  agent_deleted: string;
  agent_chat_failed: string;
  agent_group_failed: string;
  // ── World builder misc components ──
  ctx_desc_update_failed: string;
  guidance_save_failed: string;
  guidance_saved: string;
  banner_save_failed: string;
  banner_removed: string;
  banner_remove_failed: string;
  icon_save_failed: string;
  icon_removed: string;
  icon_remove_failed: string;
  src_ref_add_failed: string;
  src_ref_deleted: string;
  src_ref_update_failed: string;
  pc_updated: string;
  pc_created: string;
  pc_needs_name: string;
  adv_inst_save_failed: string;
  auto_gen_complete: string;
  voice_saved: string;
  gen_themes_generated: string;
  // ── useWorldMaps / useMapEntityActions ──
  wmap_create_failed: string;
  wmap_update_failed: string;
  wmap_delete_failed: string;
  mea_create_failed: string;
  mea_gen_failed: string;
  wh_download_failed: string;
  // ── Play components ──
  play_msg_updated: string;
  play_snapshot_downloaded: string;
  play_snapshot_failed: string;
  play_downloaded: string;
  // ── Map components ──
  map_lib_added: string;
  map_lib_removed: string;
  svg_traced: string;
  svg_lib_saved: string;
  // ── MusicLibraryPanel / imagegen ──
  music_not_auth: string;
  music_group_update_failed: string;
  draft_started: string;
  draw_seed_saved: string;
  // ── Settings / Profile / Layout ──
  byok_saved: string;
  ai_enter_key: string;
  app_bg_reset: string;
  custom_ai_saved: string;
  custom_ai_save_failed: string;
  custom_ai_name_placeholder: string;
  custom_ai_model_placeholder: string;
  custom_ai_endpoint_placeholder: string;
  custom_ai_key_placeholder: string;
  custom_ai_instructions_placeholder: string;
  settings_activity_filter_user: string;
  settings_activity_search: string;
  settings_credit_email: string;
  settings_error_search: string;
  settings_tool_search_query: string;
  settings_tool_fetch_url: string;
  settings_tool_franchise_slug: string;
  mod_notes_placeholder: string;
  mod_report_details: string;
  byok_api_key_placeholder: string;
  byok_account_name_placeholder: string;
  byok_use_default: string;
  byok_endpoint_url: string;
  ref_link_copied: string;
  copy_failed: string;
  sign_out_failed: string;
  admin_search_users: string;
  // ── Community ──
  forum_topic_created: string;
  forum_topic_failed: string;
}

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  he: "עברית",
  ru: "Русский",
};

export const LOCALE_SETTING_LABELS: Record<LocaleSetting, string> = {
  auto: "Auto",
  en: "EN",
  he: "HE",
  ru: "RU",
};

export const LOCALE_SETTING_FULL_LABELS: Record<LocaleSetting, string> = {
  auto: "Auto (detect)",
  en: "English",
  he: "עברית",
  ru: "Русский",
};

export const LOCALE_DIRECTIONS: Record<Locale, "ltr" | "rtl"> = {
  en: "ltr",
  he: "rtl",
  ru: "ltr",
};
